import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addContractLineItem, getContract, getLineItemGlDefaults, searchGlAccounts, setContractServices, updateContractLineItem, type LineItemBody } from '@/api/contracts';
import { getServices } from '@/api/services';
import { listAssets } from '@/api/assets';
import { listSoftware } from '@/api/software';
import { searchSimPlans } from '@/api/simPlans';
import { getAudit } from '@/api/audit';
import { ApiError } from '@/api/client';
import { EntityPicker, type PickerOption } from '@/components/EntityPicker';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { formatDate, formatDateTime, formatMoney } from '@/lib/dateFormat';
import type { ContractLineItem, ContractServiceLink } from '@/types/oscar';

type Tab = 'lineitems' | 'costs' | 'services' | 'files' | 'audit';

// Line-item target lookups. Assets search server-side (serial/hostname/tag);
// software pools are a small list filtered client-side; SIM plans search by
// plan name / carrier.
const assetSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  listAssets({ search: q, pageSize: 20 }, signal).then((r) => r.Items.map((a) => ({
    id: a.AssetId,
    label: a.SerialNumber || a.Hostname || a.AssetTag || `Asset #${a.AssetId}`,
    sublabel: [a.Make, a.Model].filter(Boolean).join(' ') || a.Category,
  })));
const softwareSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  listSoftware(signal).then((list) =>
    list
      .filter((p) => p.Title.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 20)
      .map((p) => ({ id: p.SoftwarePoolId, label: p.Title, sublabel: p.Vendor ?? undefined })));
const simPlanSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  searchSimPlans(q, signal).then((list) => list.map((p) => ({ id: p.SimPlanId, label: p.PlanName, sublabel: p.Carrier ?? undefined })));
// GL account picker — expense accounts from oscar.vGlAccount, shown as "code — description".
const glSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  searchGlAccounts(q, signal).then((list) => list.map((g) => ({ id: g.GlAccountId, label: `${g.AccountCode} — ${g.AccountDescription}` })));

export function ContractDetail() {
  const { id } = useParams();
  const contractId = Number(id);
  const queryClient = useQueryClient();
  const { canMutate, isFinance } = useAuth();
  // Finance may manage contract documents (upload/delete files) even though they
  // can't edit the contract record itself — mirrors AuthHelper.CanWriteAttachments.
  const canWriteFiles = canMutate || isFinance;
  const [tab, setTab] = useState<Tab>('lineitems');
  const [addOpen, setAddOpen] = useState(false);
  const [kind, setKind] = useState<LineItemBody['LineItemKind']>('Asset');
  const [targetId, setTargetId] = useState('');
  const [targetLabel, setTargetLabel] = useState('');
  const [monthly, setMonthly] = useState('');
  const [vendorRef, setVendorRef] = useState('');
  // Seats + cost per seat + entitled pools — SoftwarePool lines only.
  const [seats, setSeats] = useState('');
  const [costPerSeat, setCostPerSeat] = useState('');
  const [selectedPools, setSelectedPools] = useState<{ id: number; label: string }[]>([]);
  // GL account for the add / edit dialogs (id as string; label = "code — description").
  const [glAccountId, setGlAccountId] = useState('');
  const [glLabel, setGlLabel] = useState('');
  const [editGlAccountId, setEditGlAccountId] = useState('');
  const [editGlLabel, setEditGlLabel] = useState('');
  // The line item being edited (vendor ref / cost / description / seats / pools) — null when closed.
  const [editLine, setEditLine] = useState<ContractLineItem | null>(null);
  const [editVendorRef, setEditVendorRef] = useState('');
  const [editMonthly, setEditMonthly] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSeats, setEditSeats] = useState('');
  const [editCostPerSeat, setEditCostPerSeat] = useState('');
  const [editPools, setEditPools] = useState<{ id: number; label: string }[]>([]);

  const query = useQuery({ queryKey: ['contract', contractId], queryFn: ({ signal }) => getContract(contractId, signal) });
  const glDefaultsQuery = useQuery({ queryKey: ['line-item-gl-defaults'], queryFn: ({ signal }) => getLineItemGlDefaults(signal) });

  // Pre-fill the GL picker from the line-item kind's configured default (editable).
  const applyGlDefault = (k: LineItemBody['LineItemKind']) => {
    const d = (glDefaultsQuery.data ?? []).find((x) => x.LineItemKind === k);
    if (d) { setGlAccountId(String(d.GlAccountId)); setGlLabel(d.AccountCode ? `${d.AccountCode} — ${d.AccountDescription ?? ''}` : `#${d.GlAccountId}`); }
    else { setGlAccountId(''); setGlLabel(''); }
  };

  const mAdd = useMutation({
    mutationFn: (b: LineItemBody) => addContractLineItem(contractId, b),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['contract', contractId] }); setTargetId(''); setTargetLabel(''); setMonthly(''); setVendorRef(''); setSeats(''); setCostPerSeat(''); setSelectedPools([]); setGlAccountId(''); setGlLabel(''); setAddOpen(false); },
  });
  const mEdit = useMutation({
    // GlAccountId: a number sets it, 0 clears it (sent for every kind).
    mutationFn: () => updateContractLineItem(contractId, editLine!.LineItemId, editLine!.LineItemKind === 'SoftwarePool' ? {
      VendorRef: editVendorRef.trim() || null,
      Description: editDesc.trim() || null,
      TotalSeats: editSeats.trim() ? Number(editSeats) : null,
      CostPerSeat: editCostPerSeat.trim() ? Number(editCostPerSeat) : null,
      SoftwarePoolIds: editPools.map((p) => p.id),
      GlAccountId: editGlAccountId ? Number(editGlAccountId) : 0,
    } : {
      VendorRef: editVendorRef.trim() || null,
      Description: editDesc.trim() || null,
      MonthlyCost: editMonthly.trim() ? Number(editMonthly) : null,
      GlAccountId: editGlAccountId ? Number(editGlAccountId) : 0,
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['contract', contractId] }); setEditLine(null); },
  });

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red">Could not load this contract.</p>;
  const { Contract: c, LineItems: items, LineItemsMonthlyTotal: lineTotal } = query.data;

  const submitAdd = () => {
    const b: LineItemBody = { LineItemKind: kind, VendorRef: vendorRef.trim() || undefined };
    if (glAccountId) b.GlAccountId = Number(glAccountId);
    const n = Number(targetId) || undefined;
    if (kind === 'Asset') { b.AssetId = n; b.MonthlyCost = monthly ? Number(monthly) : undefined; }
    else if (kind === 'SimPlan') { b.SimPlanId = n; b.MonthlyCost = monthly ? Number(monthly) : undefined; }
    else {
      // SoftwarePool bundle: pools are optional now (a GL-coded software cost line
      // may have none). Cost is seats × cost per seat (server-computed).
      b.SoftwarePoolIds = selectedPools.map((p) => p.id);
      if (seats.trim()) b.TotalSeats = Number(seats);
      if (costPerSeat.trim()) b.CostPerSeat = Number(costPerSeat);
    }
    mAdd.mutate(b);
  };

  const addPool = (list: { id: number; label: string }[], id: number, label: string) =>
    list.some((p) => p.id === id) ? list : [...list, { id, label }];

  const startEditLine = (li: ContractLineItem) => {
    mEdit.reset();
    setEditLine(li);
    setEditVendorRef(li.VendorRef ?? '');
    setEditMonthly(li.MonthlyCost != null ? String(li.MonthlyCost) : '');
    setEditDesc(li.Description ?? '');
    setEditSeats(li.TotalSeats != null ? String(li.TotalSeats) : '');
    setEditCostPerSeat(li.CostPerSeat != null ? String(li.CostPerSeat) : '');
    setEditPools((li.Pools ?? []).map((p) => ({ id: p.SoftwarePoolId, label: p.Title })));
    setEditGlAccountId(li.GlAccountId != null ? String(li.GlAccountId) : '');
    setEditGlLabel(li.GlAccountId != null ? `${li.GlAccountCode ?? ''}${li.GlAccountDescription ? ` — ${li.GlAccountDescription}` : ''}`.trim() : '');
  };

  return (
    <section>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Link to="/contracts" className="text-xs text-navy hover:underline">← Contracts</Link>
          <h1 className="font-heading text-2xl font-bold text-navy">{c.ContractName}</h1>
          <p className="text-sm text-darkGrey/80">{c.Vendor} · {c.ContractType} · {c.Status}</p>
        </div>
        {canMutate && (
          <Link to={`/contracts/${contractId}/edit`} className="shrink-0 rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Edit</Link>
        )}
      </div>

      <div className="mb-4 flex gap-1 border-b border-border text-sm">
        {([['lineitems', 'Line items'], ['costs', 'Costs'], ['services', 'Services'], ['files', 'Files'], ['audit', 'Audit']] as [Tab, string][]).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={tab === k ? 'border-b-2 border-orange px-3 py-2 font-semibold text-navy' : 'px-3 py-2 text-darkGrey/70 hover:text-navy'}>{l}</button>
        ))}
      </div>

      {tab === 'lineitems' && (
        <div>
          {canMutate && <button type="button" onClick={() => { mAdd.reset(); applyGlDefault(kind); setAddOpen(true); }} className="mb-3 rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">Add line item</button>}
          <ul className="space-y-2 text-sm">
            {items.length === 0 && <li className="text-darkGrey/70">No line items.</li>}
            {items.map((li) => (
              <li key={li.LineItemId} className="flex items-center justify-between gap-3 rounded border border-border bg-white px-3 py-2">
                <span>
                  <span className="font-medium text-navy">{li.LineItemKind}</span>
                  {li.VendorRef && (
                    <span className="ml-1.5 inline-flex rounded-full bg-lightBlue/20 px-2 py-0.5 text-xs font-semibold text-navy" title="Vendor service / billing number">
                      ref {li.VendorRef}
                    </span>
                  )}
                  {li.GlAccountCode && (
                    <span className="ml-1.5 inline-flex rounded-full bg-navy/10 px-2 py-0.5 text-xs font-semibold text-navy" title={li.GlAccountDescription ?? undefined}>
                      GL {li.GlAccountCode}
                    </span>
                  )}
                  <span className="text-darkGrey/70"> · {li.Description ?? '—'}{li.MonthlyCost != null ? ` · ${formatMoney(li.MonthlyCost)}/mo` : ''}{li.LineItemKind === 'SoftwarePool' && li.TotalSeats != null ? ` · ${li.TotalSeats} seats${li.CostPerSeat != null ? ` @ ${formatMoney(li.CostPerSeat)}` : ''}` : ''}</span>
                  {li.LineItemKind === 'SoftwarePool' && li.Pools.length > 0 && (
                    <span className="ml-1.5">
                      {li.Pools.map((p) => (
                        <span key={p.SoftwarePoolId} className="mr-1 inline-flex rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green" title="Entitled licence pool">{p.Title}</span>
                      ))}
                    </span>
                  )}
                </span>
                {canMutate && (
                  <button type="button" onClick={() => startEditLine(li)} className="shrink-0 rounded border border-border bg-white px-2 py-1 text-xs font-medium text-navy hover:bg-bg">Edit</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'costs' && (
        <dl className="grid max-w-md grid-cols-2 gap-y-2 text-sm">
          <dt className="font-semibold text-darkGrey">Monthly cost</dt><dd className="text-darkGrey/80">{c.MonthlyCost ?? '—'}</dd>
          <dt className="font-semibold text-darkGrey">Annual cost</dt><dd className="text-darkGrey/80">{c.AnnualCost ?? '—'}</dd>
          <dt className="font-semibold text-darkGrey">Total value</dt><dd className="text-darkGrey/80">{c.TotalValue ?? '—'}</dd>
          <dt className="font-semibold text-darkGrey">Line items / mo</dt><dd className="text-darkGrey/80">{lineTotal}</dd>
          <dt className="font-semibold text-darkGrey">End date</dt><dd className="text-darkGrey/80">{formatDate(c.EndDate)}</dd>
        </dl>
      )}

      {tab === 'services' && (
        <ContractServicesTab
          contractId={contractId}
          current={query.data.Services ?? []}
          canMutate={canMutate}
          onSaved={() => { queryClient.invalidateQueries({ queryKey: ['contract', contractId] }); queryClient.invalidateQueries({ queryKey: ['services'] }); }}
        />
      )}

      {tab === 'files' && (
        <AttachmentsSection entityType="Contract" entityId={contractId} canWrite={canWriteFiles} />
      )}

      {tab === 'audit' && <AuditTab contractId={contractId} />}

      <AlertDialog open={addOpen} onOpenChange={setAddOpen} title="Add line item" confirmLabel="Add" closeOnConfirm={false} onConfirm={submitAdd}>
        <div className="space-y-2">
          <select aria-label="Line item kind" value={kind} onChange={(e) => { const k = e.target.value as LineItemBody['LineItemKind']; setKind(k); setTargetId(''); setTargetLabel(''); applyGlDefault(k); }} className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm">
            <option value="Asset">Asset</option>
            <option value="SoftwarePool">Software pool</option>
            <option value="SimPlan">SIM plan</option>
          </select>
          {kind === 'Asset' && (
            <EntityPicker queryKey="line-item-asset" search={assetSearch} minChars={2}
              valueLabel={targetLabel} onChange={(id, label) => { setTargetId(id ? String(id) : ''); setTargetLabel(label); }}
              placeholder="Search assets by serial / hostname / tag" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          )}
          {kind === 'SoftwarePool' && (
            <div>
              <p className="mb-1 text-xs text-darkGrey/70">Licence pools this line entitles — add more than one for a bundle:</p>
              <PoolMultiPicker selected={selectedPools}
                onAdd={(id, label) => setSelectedPools((cur) => addPool(cur, id, label))}
                onRemove={(id) => setSelectedPools((cur) => cur.filter((p) => p.id !== id))} />
            </div>
          )}
          {kind === 'SimPlan' && (
            <EntityPicker queryKey="line-item-simplan" search={simPlanSearch} minChars={1}
              valueLabel={targetLabel} onChange={(id, label) => { setTargetId(id ? String(id) : ''); setTargetLabel(label); }}
              placeholder="Search SIM plans by name / carrier" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          )}
          {kind !== 'SoftwarePool' && (
            <input aria-label="Monthly cost" value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="Monthly cost (optional)" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          )}
          <input aria-label="Vendor service number" value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} placeholder="Vendor service / billing number (optional)" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          <div>
            <p className="mb-1 text-xs text-darkGrey/70">GL account{glLabel ? '' : ' (optional)'}:</p>
            <EntityPicker queryKey="line-item-gl" search={glSearch} minChars={1}
              valueLabel={glLabel} onChange={(id, label) => { setGlAccountId(id ? String(id) : ''); setGlLabel(label); }}
              placeholder="Search GL by code or description" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          </div>
          {kind === 'SoftwarePool' && (
            <>
              <input aria-label="Total seats" type="number" value={seats} onChange={(e) => setSeats(e.target.value)} placeholder="Total seats (optional)" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
              <input aria-label="Cost per seat" type="number" step="0.01" value={costPerSeat} onChange={(e) => setCostPerSeat(e.target.value)} placeholder="Cost per seat (optional)" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
              <ComputedCost seats={seats} cost={costPerSeat} />
            </>
          )}
          {mAdd.isError && <p role="alert" className="text-xs text-red">{mAdd.error instanceof ApiError ? mAdd.error.message : 'Could not add the line item.'}</p>}
        </div>
      </AlertDialog>

      <AlertDialog open={editLine != null} onOpenChange={(o) => { if (!o) setEditLine(null); }} title="Edit line item" confirmLabel={mEdit.isPending ? 'Saving…' : 'Save'} closeOnConfirm={false} onConfirm={() => mEdit.mutate()}>
        <div className="space-y-2">
          <p className="text-xs text-darkGrey/70">{editLine?.LineItemKind} line{editLine?.LineItemKind === 'SoftwarePool' ? ' — entitled pools, seats and cost per seat (line cost = seats × cost per seat).' : ' — vendor service number, cost and description (the linked item can’t be changed here).'}</p>
          <input aria-label="Vendor service number" value={editVendorRef} onChange={(e) => setEditVendorRef(e.target.value)} placeholder="Vendor service / billing number" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          <div>
            <p className="mb-1 text-xs text-darkGrey/70">GL account:</p>
            <EntityPicker queryKey="edit-line-item-gl" search={glSearch} minChars={1}
              valueLabel={editGlLabel} onChange={(id, label) => { setEditGlAccountId(id ? String(id) : ''); setEditGlLabel(label); }}
              placeholder="Search GL by code or description" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          </div>
          {editLine?.LineItemKind !== 'SoftwarePool' && (
            <input aria-label="Monthly cost" value={editMonthly} onChange={(e) => setEditMonthly(e.target.value)} placeholder="Monthly cost" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          )}
          <input aria-label="Description" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          {editLine?.LineItemKind === 'SoftwarePool' && (
            <>
              <p className="text-xs text-darkGrey/70">Entitled licence pools:</p>
              <PoolMultiPicker selected={editPools}
                onAdd={(id, label) => setEditPools((cur) => addPool(cur, id, label))}
                onRemove={(id) => setEditPools((cur) => cur.filter((p) => p.id !== id))} />
              <input aria-label="Total seats" type="number" value={editSeats} onChange={(e) => setEditSeats(e.target.value)} placeholder="Total seats" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
              <input aria-label="Cost per seat" type="number" step="0.01" value={editCostPerSeat} onChange={(e) => setEditCostPerSeat(e.target.value)} placeholder="Cost per seat" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
              <ComputedCost seats={editSeats} cost={editCostPerSeat} />
            </>
          )}
          {mEdit.isError && <p role="alert" className="text-xs text-red">{mEdit.error instanceof ApiError ? mEdit.error.message : 'Could not save the line item.'}</p>}
        </div>
      </AlertDialog>
    </section>
  );
}

/** Multi-select of software pools a bundle line entitles: chips + a picker that clears after each add. */
function PoolMultiPicker({ selected, onAdd, onRemove }: {
  selected: { id: number; label: string }[];
  onAdd: (id: number, label: string) => void;
  onRemove: (id: number) => void;
}) {
  const [key, setKey] = useState(0);
  return (
    <div className="space-y-1.5">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <li key={p.id} className="inline-flex items-center gap-1 rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">
              {p.label}
              <button type="button" onClick={() => onRemove(p.id)} aria-label={`Remove ${p.label}`} className="text-green/70 hover:text-red">×</button>
            </li>
          ))}
        </ul>
      )}
      <EntityPicker key={key} queryKey="line-item-software" search={softwareSearch} minChars={1}
        valueLabel="" onChange={(id, label) => { if (id) { onAdd(id, label); setKey((k) => k + 1); } }}
        placeholder="Add a software pool…" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
    </div>
  );
}

/** Live "seats × cost per seat = line cost" hint under the software-pool inputs. */
function ComputedCost({ seats, cost }: { seats: string; cost: string }) {
  const s = Number(seats);
  const c = Number(cost);
  if (!seats.trim() || !cost.trim() || Number.isNaN(s) || Number.isNaN(c)) return null;
  return <p className="text-xs text-darkGrey/70">Line cost = {s} × {c} = <span className="font-semibold text-navy">{formatMoney(s * c)}</span></p>;
}

/**
 * Services this contract funds (CMDB cost showback) — the contract's recurring
 * cost rolls up to these services on the Services page. Read view + (Helpdesk/
 * Admin) an edit multi-select from the shared catalogue.
 */
function ContractServicesTab({ contractId, current, canMutate, onSaved }: {
  contractId: number;
  current: ContractServiceLink[];
  canMutate: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const catalogue = useQuery({ queryKey: ['services'], queryFn: ({ signal }) => getServices(signal), enabled: editing, retry: false });
  const mutation = useMutation({
    mutationFn: () => setContractServices(contractId, selected),
    onSuccess: () => { setEditing(false); onSaved(); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save services.'),
  });
  const startEdit = () => { setSelected(current.map((s) => s.ServiceId)); setError(null); setFilter(''); setEditing(true); };
  const toggle = (sid: number) => setSelected((p) => p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]);

  if (!editing) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-darkGrey/70">Services this contract funds — its recurring cost is shown against these on the Services page.</p>
        {current.length === 0 ? (
          <p className="text-sm text-darkGrey/70">Not linked to any service.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {current.map((s) => <li key={s.ServiceId} className="inline-flex rounded-full bg-lightBlue/20 px-2 py-0.5 text-xs font-semibold text-navy">{s.Name}</li>)}
          </ul>
        )}
        {canMutate && <button type="button" onClick={startEdit} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Edit services</button>}
      </div>
    );
  }

  const all = catalogue.data ?? [];
  const q = filter.trim().toLowerCase();
  const visible = q ? all.filter((s) => s.Name.toLowerCase().includes(q) || (s.ServiceGroup ?? '').toLowerCase().includes(q)) : all;

  return (
    <div className="space-y-2">
      {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter services…" aria-label="Filter services" className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
      <div className="max-h-60 overflow-y-auto rounded border border-border">
        {catalogue.isLoading && <p className="px-3 py-2 text-xs text-darkGrey/60">Loading catalogue…</p>}
        {catalogue.isError && <p className="px-3 py-2 text-xs text-red">Could not load the service catalogue.</p>}
        {visible.map((s) => (
          <label key={s.ServiceId} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-bg">
            <input type="checkbox" checked={selected.includes(s.ServiceId)} onChange={() => toggle(s.ServiceId)} />
            <span>{s.Name}{s.ServiceGroup ? <span className="text-darkGrey/50"> · {s.ServiceGroup}</span> : null}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" disabled={mutation.isPending} onClick={() => { setError(null); mutation.mutate(); }} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">{mutation.isPending ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={() => setEditing(false)} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
      </div>
    </div>
  );
}

function AuditTab({ contractId }: { contractId: number }) {
  const query = useQuery({ queryKey: ['audit', 'Contract', contractId], queryFn: ({ signal }) => getAudit({ entityType: 'Contract', entityId: contractId }, signal) });
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load audit history.</p>;
  if (!query.data || query.data.length === 0) return <p className="text-sm text-darkGrey/70">No audit history.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {query.data.map((e) => (
        <li key={e.AuditId} className="rounded border border-border bg-white px-3 py-2">
          <span className="font-semibold text-navy">{e.Action}</span>
          <span className="text-darkGrey/70"> · {formatDateTime(e.ChangedDate)} · {e.ChangedBy}</span>
        </li>
      ))}
    </ul>
  );
}
